import React, { useState, useRef, useEffect } from 'react';
import { Chip, ChipProps } from '@mui/material';

interface MarqueeChipProps extends ChipProps {
  label: string;
  maxWidth?: number;
  textColor?: string; // Add textColor prop
}

const MarqueeChip: React.FC<MarqueeChipProps> = ({ 
  label, 
  maxWidth = 120, 
  textColor,
  ...chipProps 
}) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const [animationOffset, setAnimationOffset] = useState(0);

  // UNIVERSAL: All pills animate on hover - no overflow detection needed
  useEffect(() => {
    // Always enable animation for all pills
    setIsOverflowing(true);
  }, [label, maxWidth]);

  // Animation loop for marquee effect
  useEffect(() => {
    let animationId: number;
    
    if (isHovered && textRef.current) {
      const textWidth = textRef.current.scrollWidth;
      const containerWidth = maxWidth - 24;
      const hiddenWidth = Math.max(0, textWidth - containerWidth);
      
      // UNIVERSAL ANIMATION: Calculate exact offset to show ALL letters
      // Use the actual text width and ensure we scroll enough to reveal everything
      const actualHiddenWidth = textWidth - containerWidth;
      const maxOffset = actualHiddenWidth > 0 
        ? actualHiddenWidth + 35  // For overflowing text: scroll past all hidden text + generous buffer
        : 25; // For text that fits: small movement for visual feedback
      
      let direction = 1;
      let currentOffset = 0;
      
      // SIMPLIFIED SPEED: Consistent slow speed for all pills
      const animationSpeed = 0.18; // Slower speed for all pills
      
      const animate = () => {
        currentOffset += direction * animationSpeed;
        
        // Reverse direction when reaching boundaries with pause
        if (currentOffset >= maxOffset) {
          direction = -1;
        } else if (currentOffset <= 0) {
          direction = 1;
        }
        
        setAnimationOffset(currentOffset);
        
        if (isHovered) { // Continue as long as hovered
          animationId = requestAnimationFrame(animate);
        }
      };
      
      // Start animation immediately
      animate();
      
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    } else {
      // Reset position when not hovered
      setAnimationOffset(0);
    }
  }, [isHovered, isOverflowing, maxWidth]);

  return (
    <Chip
      {...chipProps}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      label={
        <span
          ref={textRef}
          style={{
            display: 'inline-block',
            transform: isHovered && isOverflowing 
              ? `translateX(-${animationOffset}px)` 
              : 'translateX(0)',
            transition: isHovered ? 'none' : 'transform 0.3s ease',
            whiteSpace: 'nowrap',
            // Preserve all existing text styling
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: textColor || 'inherit', // Use the textColor prop directly
            lineHeight: 'inherit',
            textAlign: 'inherit',
            // Set CSS custom property for forced color
            '--pill-text-color': textColor || 'inherit',
          } as React.CSSProperties}
          className={`pill-text-span ${textColor ? 'pill-text-forced-color' : ''}`}
          data-text-color={textColor}
        >
          {label}
        </span>
      }
      sx={{
        // Ensure the chip maintains its exact current styling for grid
        width: `${maxWidth}px`,
        minWidth: `${maxWidth}px`,
        maxWidth: `${maxWidth}px`,
        overflow: 'hidden', // Keep text contained within pill
        '& .MuiChip-label': {
          // Preserve all existing label styling
          padding: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: textColor || 'inherit', // Use textColor prop directly
          lineHeight: 'inherit',
          textAlign: 'inherit',
          // Ensure text stays within bounds
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        // Don't override any existing chip styles
        ...chipProps.sx,
      }}
    />
  );
};

export default MarqueeChip;
